import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FileDown, Shield, Droplets, HardHat, AlertTriangle, PlayCircle, X } from "lucide-react";
import previewNachbehandlung from "@/assets/preview-nachbehandlung.jpg";
import previewWasserzugabe from "@/assets/preview-wasserzugabe.jpg";
import previewSicherheit from "@/assets/preview-sicherheit.jpg";

const infos = [
  {
    icon: HardHat,
    title: "Nachbehandlung von Beton",
    description:
      "Erfahren Sie, wie Sie Ihren Beton richtig nachbehandeln, damit er seine volle Festigkeit erreicht. Schutz vor Austrocknung und Frost ist entscheidend.",
    downloadLabel: "PDF herunterladen",
    href: "/downloads/beton2go_nachbehandlung.pdf",
    preview: previewNachbehandlung,
    requiresConsent: true,
  },
  {
    icon: Droplets,
    title: "Wasserzugabe",
    description:
      "Warum die richtige Wassermenge entscheidend ist und wann auf keinen Fall Wasser nachgegeben werden darf. Wichtige Hinweise für die Verarbeitung.",
    downloadLabel: "PDF herunterladen",
    href: "/downloads/beton2go_wasserzugabe.pdf",
    preview: previewWasserzugabe,
    requiresConsent: false,
  },
  {
    icon: Shield,
    title: "Sicherheitsdatenblatt",
    description:
      "Alle wichtigen Sicherheitsinformationen für den Umgang mit Frischbeton. Schutzausrüstung, Erste Hilfe und Entsorgungshinweise.",
    downloadLabel: "PDF herunterladen",
    href: "/downloads/beton2go_sicherheitsdatenblatt.pdf",
    preview: previewSicherheit,
    requiresConsent: false,
  },
];

const Betoninfos = () => {
  const [consentHref, setConsentHref] = useState<string | null>(null);

  useEffect(() => {
    if (!consentHref) return;
    const original = document.body.style.overflow;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setConsentHref(null);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", onKey);
    };
  }, [consentHref]);

  const confirmDownload = () => {
    if (!consentHref) return;
    const a = document.createElement("a");
    a.href = consentHref;
    a.download = "";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setConsentHref(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16">
          <div className="container mx-auto px-4 text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="font-display text-5xl md:text-6xl font-bold mb-4">
                Beton<span className="text-primary">infos</span>
              </h1>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Wichtige Dokumente rund um Verarbeitung und Sicherheit.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Downloads with Preview Images */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-12 text-center">
              Downloads & <span className="text-primary">Dokumente</span>
            </h2>
            <div className="flex flex-col gap-10 max-w-5xl mx-auto">
              {infos.map((info, i) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                  className="bg-card border border-border overflow-hidden group hover:border-primary/40 transition-colors"
                >
                  <div className="grid md:grid-cols-[1fr_250px] gap-0">
                    {/* Info */}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="w-14 h-14 bg-primary/10 flex items-center justify-center mb-6">
                        <info.icon className="h-7 w-7 text-primary" />
                      </div>
                      <h3 className="font-display text-xl font-bold mb-3">{info.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                        {info.description}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {info.requiresConsent ? (
                          <button
                            type="button"
                            onClick={() => setConsentHref(info.href)}
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 font-display text-sm tracking-wider hover:bg-primary/80 transition-colors w-fit"
                          >
                            <FileDown className="h-4 w-4" />
                            {info.downloadLabel}
                          </button>
                        ) : (
                          <a
                            href={info.href}
                            download
                            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 font-display text-sm tracking-wider hover:bg-primary/80 transition-colors w-fit"
                          >
                            <FileDown className="h-4 w-4" />
                            {info.downloadLabel}
                          </a>
                        )}
                        <Link
                          to="/funktion#video"
                          className="inline-flex items-center gap-2 border border-primary/40 text-foreground px-4 py-2 font-display text-sm tracking-wider hover:border-primary hover:text-primary transition-colors w-fit"
                        >
                          <PlayCircle className="h-4 w-4 text-primary" />
                          Zum Erklär-Video
                        </Link>
                      </div>
                    </div>
                    {/* Preview Image */}
                    <div className="border-t md:border-t-0 md:border-l border-border bg-muted/30 p-4 flex items-center justify-center">
                      <img
                        src={info.preview}
                        alt={`Vorschau: ${info.title}`}
                        className="w-full h-auto max-h-[350px] object-contain shadow-lg border border-border"
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Haftungshinweis-Modal */}
      {consentHref && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="haftungshinweis-title"
          onClick={() => setConsentHref(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="relative max-w-lg w-full bg-card border border-primary/40 p-6 md:p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setConsentHref(null)}
              aria-label="Schließen"
              className="absolute right-3 top-3 h-9 w-9 inline-flex items-center justify-center rounded-full border border-white/10 bg-background/60 text-foreground hover:bg-background"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 flex items-center justify-center bg-primary/10 border border-primary/30">
                <AlertTriangle className="h-6 w-6 text-primary" />
              </div>
              <h2 id="haftungshinweis-title" className="font-display text-xl md:text-2xl font-bold uppercase tracking-wide">
                Wichtiger Haftungshinweis
              </h2>
            </div>
            <div className="space-y-3 text-sm md:text-base leading-relaxed text-muted-foreground">
              <p>
                Die Verantwortung für die fachgerechte Nachbehandlung des Betons liegt vollständig
                beim Kunden.
              </p>
              <p>
                Beton2Go Romano übernimmt keine Haftung für Schäden, die durch falsche oder
                fehlende Nachbehandlung entstehen.
              </p>
              <p>
                Weitere Informationen finden Sie hier unter <span className="text-foreground font-medium">Betoninfos</span>.
              </p>
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setConsentHref(null)}
                className="inline-flex items-center justify-center border border-border px-4 py-2 font-display text-sm tracking-wider hover:border-primary/50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={confirmDownload}
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2 font-display text-sm tracking-wider hover:bg-primary/80 transition-colors"
              >
                <FileDown className="h-4 w-4" />
                Verstanden – PDF herunterladen
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Betoninfos;
